import React from 'react';
import clsx from 'clsx';
import { Tooltip as TooltipWrapper, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

type AnalyticsActionButtonState = 'default' | 'selected' | 'disabled';

interface AnalyticsActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  selectedIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  state?: AnalyticsActionButtonState;
  showTooltip?: boolean;
}

export const AnalyticsActionButton: React.FC<AnalyticsActionButtonProps> = ({
  icon: Icon,
  selectedIcon: SelectedIcon,
  label,
  state = 'default',
  showTooltip = true,
  className,
  ...rest
}) => {
  const isSelected = state === 'selected';
  const isDisabled = state === 'disabled';

  const ActiveIcon = isSelected && SelectedIcon ? SelectedIcon : Icon;

  const buttonElement = (
    <button
      type="button"
      className={clsx(
        'inline-flex h-9 w-9 flex-col items-center justify-center rounded-md',
        isSelected && 'cursor-pointer bg-indigo-500 text-white',
        isDisabled && 'cursor-not-allowed text-neutral-300',
        !isSelected && !isDisabled && 'cursor-pointer text-neutral-500 hover:bg-neutral-100',
        className,
      )}
      disabled={isDisabled}
      aria-label={label}
      {...rest}
    >
      <ActiveIcon className="h-5.5 w-5.5" />
    </button>
  );

  if (showTooltip && !isDisabled) {
    return (
      <TooltipWrapper>
        <TooltipTrigger asChild>{buttonElement}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={4}
          className="font-sans text-sm leading-4 font-normal text-white dark:text-neutral-900"
        >
          {label}
        </TooltipContent>
      </TooltipWrapper>
    );
  }

  return buttonElement;
};
